import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGetTagsQuery, useGetDealStagesQuery } from "@/store/rest_api";
import type { WorkflowTriggerType } from "@/model/interface/Workflow";
import WebhookCredentialsManager from "@/components/pages/workflows/WebhookCredentialsManager";

interface TriggerConfigEditorProps {
  triggerType: WorkflowTriggerType;
  config: Record<string, string>;
  onChange: (config: Record<string, string>) => void;
  workflowId?: string;
}

const TriggerConfigEditor: React.FC<TriggerConfigEditorProps> = ({ triggerType, config, onChange, workflowId }) => {
  const { data: tags = [] } = useGetTagsQuery({});
  const { data: stages = [] } = useGetDealStagesQuery({});

  switch (triggerType) {
    case "TAG_ADDED":
      return (
        <div>
          <Label className="text-xs">When this tag is added</Label>
          <Select
            value={config.tagId || ""}
            onValueChange={(value) => onChange({ tagId: value === "ANY" ? "" : value })}
          >
            <SelectTrigger className="mt-1"><SelectValue placeholder="Any tag" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ANY">Any tag</SelectItem>
              {tags.map((tag) => (
                <SelectItem key={tag.id} value={tag.id}>{tag.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    case "DEAL_STAGE_CHANGE":
      return (
        <div>
          <Label className="text-xs">When deal moves to stage</Label>
          <Select
            value={config.stageId || ""}
            onValueChange={(value) => onChange({ stageId: value === "ANY" ? "" : value })}
          >
            <SelectTrigger className="mt-1"><SelectValue placeholder="Any stage" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ANY">Any stage</SelectItem>
              {stages.map((stage) => (
                <SelectItem key={stage.id} value={stage.id}>{stage.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    case "WEBHOOK_RECEIVED":
      if (!workflowId) {
        return (
          <p className="text-xs text-muted-foreground">
            Save the workflow first to mint webhook tokens.
          </p>
        );
      }
      return <WebhookCredentialsManager workflowId={workflowId} />;
    case "TASK_REMINDER":
      return (
        <p className="text-muted-foreground text-xs">
          Fires once for each task that is due today or overdue and not yet done, notifying the task's assignee.
        </p>
      );
    case "LEAD_CONVERTED":
      return (
        <p className="text-muted-foreground text-xs">
          Fires once when a lead becomes a contact: their deal moves into a Pipeline or Won stage, or someone clicks
          Convert to contact.
        </p>
      );
    case "LEAD_STALE":
      return (
        <div>
          <Label className="text-xs">Consider a lead stale after (days without contact)</Label>
          <Input
            type="number"
            min={1}
            value={config.staleDays || "30"}
            onChange={(value) => onChange({ staleDays: value.target.value })}
            className="mt-1"
          />
          <p className="text-muted-foreground mt-1 text-xs">
            Fires once for each Lead with no call or message in this many days.
          </p>
        </div>
      );
    default:
      return null;
  }
};

export default TriggerConfigEditor;
